/** Styled in-app dialogs (replaces window.alert / confirm / prompt). */

import { escapeHtml } from './escapeHtml';
import { promptTextEditor } from './itemTextModal';

export type AppDialogChoice = { value: string; label: string; hint?: string };

type DialogMode = 'alert' | 'confirm' | 'choose';

type DialogOpts = {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  placeholder?: string;
  defaultValue?: string;
  inputLabel?: string;
  choices?: AppDialogChoice[];
  /** Grid layout for emoji / compact icon pickers. */
  choiceLayout?: 'icons';
};

let modalEl: HTMLElement | null = null;
let titleEl: HTMLElement | null = null;
let bodyEl: HTMLElement | null = null;
let actionsEl: HTMLElement | null = null;
let resolveDialog: ((value: unknown) => void) | null = null;
let activeMode: DialogMode = 'alert';
let chain: Promise<unknown> = Promise.resolve();

function formatMessageHtml(message: string): string {
  return message
    .split(/\n\n+/)
    .map(block => `<p class="app-dialog-message">${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function ensureModal(): HTMLElement {
  if (modalEl) return modalEl;

  const modal = document.createElement('div');
  modal.id = 'app-dialog';
  modal.className = 'app-dialog hidden';
  modal.setAttribute('role', 'presentation');
  modal.innerHTML = `
    <div class="app-dialog-backdrop" aria-hidden="true"></div>
    <div class="app-dialog-card" role="dialog" aria-modal="true" aria-labelledby="app-dialog-title">
      <h2 id="app-dialog-title" class="app-dialog-title"></h2>
      <div class="app-dialog-body"></div>
      <div class="app-dialog-actions"></div>
    </div>
  `;

  titleEl = modal.querySelector('.app-dialog-title')!;
  bodyEl = modal.querySelector('.app-dialog-body')!;
  actionsEl = modal.querySelector('.app-dialog-actions')!;
  modal.querySelector('.app-dialog-backdrop')!.addEventListener('click', () => {
    if (activeMode === 'alert') finish(undefined);
    else if (activeMode === 'confirm') finish(false);
    else finish(null);
  });

  document.body.appendChild(modal);
  modalEl = modal;
  return modal;
}

function finish(value: unknown): void {
  if (!modalEl || !resolveDialog) return;
  modalEl.classList.add('hidden');
  document.body.style.overflow = '';
  const r = resolveDialog;
  resolveDialog = null;
  r(value);
}

function enqueue<T>(fn: () => Promise<T>): Promise<T> {
  const p = chain.then(fn) as Promise<T>;
  chain = p.catch(() => {});
  return p;
}

function showDialog(mode: DialogMode, opts: DialogOpts): Promise<unknown> {
  return enqueue(() => new Promise(resolve => {
    ensureModal();
    activeMode = mode;
    resolveDialog = resolve;

    titleEl!.textContent = opts.title;
    bodyEl!.innerHTML = opts.message ? formatMessageHtml(opts.message) : '';
    actionsEl!.innerHTML = '';

    const addBtn = (
      label: string,
      className: string,
      onClick: () => void,
      primary = false
    ) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = className;
      btn.textContent = label;
      if (primary) btn.classList.add('app-dialog-btn-primary');
      btn.addEventListener('click', onClick);
      actionsEl!.appendChild(btn);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (mode === 'alert') finish(undefined);
        else finish(mode === 'choose' ? null : false);
      }
    };

    document.addEventListener('keydown', onKey, { once: true });

    if (mode === 'alert') {
      addBtn(opts.confirmLabel ?? 'OK', 'btn-primary app-dialog-btn', () => finish(undefined), true);
    } else if (mode === 'confirm') {
      const danger = opts.danger ?? false;
      addBtn(opts.cancelLabel ?? 'Cancel', 'btn-ghost-dark app-dialog-btn', () => finish(false));
      addBtn(
        opts.confirmLabel ?? 'OK',
        danger ? 'btn-primary app-dialog-btn app-dialog-btn-danger' : 'btn-primary app-dialog-btn',
        () => finish(true),
        true
      );
    } else if (mode === 'choose') {
      const list = document.createElement('ul');
      list.className =
        opts.choiceLayout === 'icons' ? 'app-dialog-choices app-dialog-choices--icons' : 'app-dialog-choices';
      for (const c of opts.choices ?? []) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'app-dialog-choice';
        btn.innerHTML = c.hint
          ? `<span class="app-dialog-choice-label">${escapeHtml(c.label)}</span><span class="app-dialog-choice-hint">${escapeHtml(c.hint)}</span>`
          : `<span class="app-dialog-choice-label">${escapeHtml(c.label)}</span>`;
        btn.addEventListener('click', () => finish(c.value));
        li.appendChild(btn);
        list.appendChild(li);
      }
      bodyEl!.appendChild(list);
      addBtn(opts.cancelLabel ?? 'Cancel', 'btn-ghost-dark app-dialog-btn app-dialog-btn-full', () => finish(null));
    }

    modalEl!.classList.remove('hidden');
    document.body.style.overflow = 'hidden';

    const primary = actionsEl!.querySelector<HTMLButtonElement>('.app-dialog-btn-primary, .btn-primary');
    primary?.focus();
  }));
}

export function mountAppDialog(): void {
  ensureModal();
}

export function appAlert(message: string, title = 'Notice'): Promise<void> {
  return showDialog('alert', { title, message }).then(() => undefined);
}

export function appConfirm(
  message: string,
  opts: {
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    danger?: boolean;
  } = {}
): Promise<boolean> {
  const dialogOpts: DialogOpts = {
    title: opts.title ?? 'Confirm',
    message,
  };
  if (opts.confirmLabel) dialogOpts.confirmLabel = opts.confirmLabel;
  if (opts.cancelLabel) dialogOpts.cancelLabel = opts.cancelLabel;
  if (opts.danger) dialogOpts.danger = opts.danger;
  return showDialog('confirm', dialogOpts).then(v => Boolean(v));
}

export type PassphrasePromptResult = {
  passphrase: string;
  includeKeyInUrl: boolean;
};

/** Passphrase entry for encrypted share links (password field, not the text editor modal). */
export function appPromptPassphrase(
  opts: {
    title: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Checkbox at top of dialog. */
    includeKeyInUrlLabel?: string;
    includeKeyInUrlDefault?: boolean;
    /** Shown when the checkbox is checked (e.g. public-link warning). */
    includeKeyInUrlWarning?: string;
  }
): Promise<PassphrasePromptResult | null> {
  return enqueue(() => new Promise(resolve => {
    ensureModal();
    activeMode = 'confirm';
    resolveDialog = v =>
      resolve(v && typeof v === 'object' && 'passphrase' in v ? (v as PassphrasePromptResult) : null);

    titleEl!.textContent = opts.title;
    bodyEl!.innerHTML = '';
    actionsEl!.innerHTML = '';

    const checkLabel = document.createElement('label');
    checkLabel.className = 'app-dialog-check';
    const checkBox = document.createElement('input');
    checkBox.type = 'checkbox';
    checkBox.checked = opts.includeKeyInUrlDefault === true;
    checkLabel.appendChild(checkBox);
    checkLabel.append(
      document.createTextNode(
        ` ${opts.includeKeyInUrlLabel ?? 'Include passphrase in the link'}`
      )
    );
    bodyEl!.appendChild(checkLabel);

    const warnEl = document.createElement('p');
    warnEl.className = 'app-dialog-warning hidden';
    warnEl.setAttribute('role', 'alert');
    const defaultWarn =
      'This makes a public link: anyone with the link can view the content without entering the passphrase.';
    warnEl.textContent = opts.includeKeyInUrlWarning ?? defaultWarn;
    bodyEl!.appendChild(warnEl);

    const syncWarning = () => {
      warnEl.classList.toggle('hidden', !checkBox.checked);
    };
    checkBox.addEventListener('change', syncWarning);
    syncWarning();

    if (opts.message) {
      const msgWrap = document.createElement('div');
      msgWrap.className = 'app-dialog-message-block';
      msgWrap.innerHTML = formatMessageHtml(opts.message);
      bodyEl!.appendChild(msgWrap);
    }

    const wrap = document.createElement('div');
    wrap.className = 'app-dialog-field';
    const fieldLabel = document.createElement('label');
    fieldLabel.className = 'app-dialog-field-label';
    fieldLabel.textContent = 'Passphrase';
    const input = document.createElement('input');
    input.type = 'password';
    input.className = 'app-dialog-input';
    input.autocomplete = 'off';
    input.spellcheck = false;
    wrap.appendChild(fieldLabel);
    wrap.appendChild(input);
    bodyEl!.appendChild(wrap);

    const addBtn = (
      labelText: string,
      className: string,
      onClick: () => void,
      primary = false
    ) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = className;
      btn.textContent = labelText;
      if (primary) btn.classList.add('app-dialog-btn-primary');
      btn.addEventListener('click', onClick);
      actionsEl!.appendChild(btn);
    };

    const finish = (value: PassphrasePromptResult | null) => {
      modalEl!.classList.add('hidden');
      document.body.style.overflow = '';
      const r = resolveDialog;
      resolveDialog = null;
      r?.(value);
    };

    addBtn(opts.cancelLabel ?? 'Cancel', 'btn-ghost-dark app-dialog-btn', () => finish(null));
    addBtn(opts.confirmLabel ?? 'Generate', 'btn-primary app-dialog-btn', () => {
      const v = input.value.trim();
      if (!v) return;
      finish({ passphrase: v, includeKeyInUrl: checkBox.checked });
    }, true);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish(null);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const v = input.value.trim();
        if (v) finish({ passphrase: v, includeKeyInUrl: checkBox.checked });
      }
    };
    document.addEventListener('keydown', onKey, { once: true });

    modalEl!.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    input.focus();
  }));
}

/** Passphrase for encrypting data to copy (no URL options). */
export function appPromptPassword(
  opts: {
    title: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    fieldLabel?: string;
  }
): Promise<string | null> {
  return enqueue(() => new Promise(resolve => {
    ensureModal();
    activeMode = 'confirm';
    resolveDialog = v => resolve(typeof v === 'string' ? v : null);

    titleEl!.textContent = opts.title;
    bodyEl!.innerHTML = '';
    actionsEl!.innerHTML = '';

    if (opts.message) {
      const msgWrap = document.createElement('div');
      msgWrap.className = 'app-dialog-message-block';
      msgWrap.innerHTML = formatMessageHtml(opts.message);
      bodyEl!.appendChild(msgWrap);
    }

    const wrap = document.createElement('div');
    wrap.className = 'app-dialog-field';
    const fieldLabel = document.createElement('label');
    fieldLabel.className = 'app-dialog-field-label';
    fieldLabel.textContent = opts.fieldLabel ?? 'Passphrase';
    const input = document.createElement('input');
    input.type = 'password';
    input.className = 'app-dialog-input';
    input.autocomplete = 'new-password';
    input.spellcheck = false;
    wrap.appendChild(fieldLabel);
    wrap.appendChild(input);
    bodyEl!.appendChild(wrap);

    const addBtn = (
      labelText: string,
      className: string,
      onClick: () => void,
      primary = false
    ) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = className;
      btn.textContent = labelText;
      if (primary) btn.classList.add('app-dialog-btn-primary');
      btn.addEventListener('click', onClick);
      actionsEl!.appendChild(btn);
    };

    const finish = (value: string | null) => {
      modalEl!.classList.add('hidden');
      document.body.style.overflow = '';
      const r = resolveDialog;
      resolveDialog = null;
      r?.(value);
    };

    addBtn(opts.cancelLabel ?? 'Cancel', 'btn-ghost-dark app-dialog-btn', () => finish(null));
    addBtn(opts.confirmLabel ?? 'OK', 'btn-primary app-dialog-btn', () => {
      const v = input.value.trim();
      if (!v) return;
      finish(v);
    }, true);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish(null);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const v = input.value.trim();
        if (v) finish(v);
      }
    };
    document.addEventListener('keydown', onKey, { once: true });

    modalEl!.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    input.focus();
  }));
}

export function appPrompt(
  message: string,
  opts: {
    title?: string;
    inputLabel?: string;
    placeholder?: string;
    defaultValue?: string;
    confirmLabel?: string;
    cancelLabel?: string;
  } = {}
): Promise<string | null> {
  const title = opts.title ?? message;
  const modalMessage =
    opts.title && message !== opts.title
      ? message
      : opts.inputLabel && !opts.title
        ? opts.inputLabel
        : undefined;
  const placeholder = opts.placeholder ?? (opts.inputLabel && opts.title ? opts.inputLabel : undefined);
  return enqueue(() => {
    const editorOpts: Parameters<typeof promptTextEditor>[0] = { title, singleLine: true };
    if (modalMessage) editorOpts.message = modalMessage;
    if (placeholder) editorOpts.placeholder = placeholder;
    if (opts.defaultValue) editorOpts.defaultValue = opts.defaultValue;
    return promptTextEditor(editorOpts);
  });
}

export function appChoose(
  opts: {
    title: string;
    message?: string;
    choices: AppDialogChoice[];
    cancelLabel?: string;
    choiceLayout?: 'icons';
  }
): Promise<string | null> {
  const dialogOpts: DialogOpts = { title: opts.title, choices: opts.choices };
  if (opts.message) dialogOpts.message = opts.message;
  if (opts.cancelLabel) dialogOpts.cancelLabel = opts.cancelLabel;
  if (opts.choiceLayout) dialogOpts.choiceLayout = opts.choiceLayout;
  return showDialog('choose', dialogOpts).then(v => (typeof v === 'string' ? v : null));
}
